import { useEffect, useRef, useState, type MouseEvent, type SVGProps } from 'react';
import { cn } from '../../lib/cn';
import { DropdownMenu } from '../dropdown-menu';
import {
  SIDEBAR_COLLAPSED_NAV_ICON_CLASS,
  SIDEBAR_NAV_ICON_CLASS,
  isSidebarGroupActive,
  sidebarActiveGroupIds,
} from './sidebar.helpers';
import type { SidebarNavGroupItem, SidebarProps } from './sidebar.types';

const ChevronDownIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronLeftIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronUpIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m18 15-6-6-6 6" />
  </svg>
);

const XMarkIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ArrowRightOnRectangleIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 3h4.5A1.5 1.5 0 0 1 21 4.5v15a1.5 1.5 0 0 1-1.5 1.5H15"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="m10 17 5-5-5-5M15 12H3" />
  </svg>
);

function SidebarBadge({
  ariaLabel,
  className,
  label,
}: {
  ariaLabel?: string;
  className?: string;
  label: string;
}) {
  return (
    <span
      aria-label={ariaLabel}
      className={cn(
        'grid min-w-5 place-items-center rounded-full bg-[var(--a-primary)] px-1.5 text-[11px] font-semibold leading-5 text-[var(--a-primary-fg)]',
        className,
      )}
    >
      {label}
    </span>
  );
}

function handleAnchorNavigate(
  event: MouseEvent<HTMLAnchorElement>,
  path: string,
  onNavigate: SidebarProps['onNavigate'],
  onMobileOpenChange: SidebarProps['onMobileOpenChange'],
) {
  if (onNavigate) {
    event.preventDefault();
    onNavigate(path);
  }
  onMobileOpenChange(false);
}

export function Sidebar({
  items,
  user,
  mobileOpen,
  collapsed,
  utilityItems = [],
  workingBranchSelector,
  userMenuTriggerRef,
  labels,
  onMobileOpenChange,
  onCollapsedChange,
  onNavigate,
  onSignOut,
}: SidebarProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const brand = labels?.brand ?? 'Invento';
  const openUserMenuLabel = [labels?.openUserMenu ?? 'Open user menu', user?.badgeAriaLabel]
    .filter(Boolean)
    .join(', ');
  const workingBranchLabel = labels?.workingBranchLabel ?? 'Working from';
  const selectedWorkingBranch = workingBranchSelector?.options.find(
    (option) => option.id === workingBranchSelector.selectedId,
  );
  const workingBranchName =
    selectedWorkingBranch?.label ??
    (workingBranchSelector?.loading
      ? (labels?.workingBranchLoading ?? 'Loading branches')
      : (labels?.workingBranchUnavailable ?? labels?.workingBranchName ?? 'All branches'));
  const canSelectWorkingBranch =
    Boolean(workingBranchSelector) &&
    !workingBranchSelector?.loading &&
    !workingBranchSelector?.disabled &&
    (workingBranchSelector?.options.length ?? 0) > 1;
  const workingBranchItems =
    workingBranchSelector?.options.map((option) => ({
      key: option.id,
      label: option.label,
      disabled: option.id === workingBranchSelector.selectedId,
      onClick: () => workingBranchSelector.onSelect(option.id),
    })) ?? [];

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleDown = (event: globalThis.MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [userMenuOpen]);

  useEffect(() => {
    const activeGroupIds = sidebarActiveGroupIds(items);
    if (activeGroupIds.length === 0) return;
    setExpandedGroups((current) => {
      let changed = false;
      const next = { ...current };
      activeGroupIds.forEach((id) => {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [items]);

  function isGroupExpanded(item: SidebarNavGroupItem) {
    return expandedGroups[item.id] ?? true;
  }

  function toggleGroup(item: SidebarNavGroupItem) {
    if (collapsed) {
      onCollapsedChange(false);
      setExpandedGroups((current) => ({ ...current, [item.id]: true }));
      return;
    }
    if (isSidebarGroupActive(item)) {
      setExpandedGroups((current) => ({ ...current, [item.id]: true }));
      return;
    }
    setExpandedGroups((current) => ({ ...current, [item.id]: !(current[item.id] ?? true) }));
  }

  function handleSignOut() {
    setUserMenuOpen(false);
    onMobileOpenChange(false);
    onSignOut();
  }

  return (
    <>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => onMobileOpenChange(false)}
        />
      ) : null}

      <aside
        data-testid="app-sidebar"
        aria-label={labels?.primaryNavigation ?? 'Primary navigation'}
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-[var(--a-line)] bg-[var(--a-surface)] transition-all duration-300 [font-family:var(--a-body)]',
          collapsed ? 'w-16' : 'w-60',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-[var(--a-line-soft)] px-[18px] py-[18px]">
          {!collapsed ? (
            <span className="flex items-center gap-2.5 text-base font-semibold tracking-normal text-[var(--a-text)]">
              <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-[var(--a-primary)] text-base font-semibold text-[var(--a-primary-fg)]">
                {brand.charAt(0).toUpperCase()}
              </span>
              {brand}
            </span>
          ) : (
            <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-[var(--a-primary)] text-base font-semibold text-[var(--a-primary-fg)]">
              {brand.charAt(0).toUpperCase()}
            </span>
          )}

          {!collapsed ? (
            <button
              className="hidden h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-[var(--a-text-faint)] transition-colors hover:bg-[var(--a-surface-2)] hover:text-[var(--a-text-soft)] lg:flex"
              onClick={() => onCollapsedChange(true)}
              aria-label={labels?.collapseNavigation ?? 'Collapse navigation'}
              type="button"
            >
              <ChevronLeftIcon className={SIDEBAR_NAV_ICON_CLASS} />
            </button>
          ) : null}

          <button
            className="rounded-lg p-1.5 text-[var(--a-text-faint)] hover:bg-[var(--a-surface-2)] lg:hidden"
            onClick={() => onMobileOpenChange(false)}
            aria-label={labels?.closeNavigation ?? 'Close navigation'}
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {user && !collapsed ? (
          <div className="border-b border-[var(--a-line-soft)] px-3 py-2.5">
            {canSelectWorkingBranch ? (
              <DropdownMenu
                align="start"
                className="rounded-[12px]"
                minWidth={216}
                items={workingBranchItems}
                trigger={
                  <button
                    type="button"
                    aria-label={`${workingBranchLabel}: ${workingBranchName}`}
                    className="flex w-[13.5rem] max-w-full items-center gap-2 rounded-[10px] border border-[var(--a-line)] bg-transparent px-2.5 py-2 text-left transition-colors hover:border-[var(--a-primary)] hover:bg-[var(--a-surface-2)]"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--a-text-faint)]">
                        {workingBranchLabel}
                      </span>
                      <span className="mt-0.5 block truncate text-[13px] font-medium text-[var(--a-text)]">
                        {workingBranchName}
                      </span>
                    </span>
                    <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-[var(--a-text-faint)]" />
                  </button>
                }
              />
            ) : (
              <div
                aria-label={`${workingBranchLabel}: ${workingBranchName}`}
                className="rounded-[10px] border border-[var(--a-line)] px-2.5 py-2 text-left"
              >
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--a-text-faint)]">
                  {workingBranchLabel}
                </p>
                <p className="mt-0.5 truncate text-[13px] font-medium text-[var(--a-text)]">
                  {workingBranchName}
                </p>
              </div>
            )}
          </div>
        ) : null}

        <nav
          className={cn(
            'flex-1 px-2.5 py-3.5',
            collapsed ? 'overflow-visible' : 'sidebar-scrollbar overflow-y-auto',
          )}
          aria-label="Primary"
        >
          <ul role="list" className="m-0 flex list-none flex-col gap-0.5 p-0">
            {items.map((item, index) => {
              if (item.type === 'group') {
                const isActive = isSidebarGroupActive(item);
                const expanded = isGroupExpanded(item);
                const CollapsedIcon = item.collapsedIcon;
                const showCollapsedFlyout = collapsed && hoveredGroupId === item.id;

                return (
                  <li
                    key={item.id}
                    className="relative"
                    onMouseEnter={() => {
                      if (collapsed) setHoveredGroupId(item.id);
                    }}
                    onMouseLeave={() => {
                      if (collapsed) setHoveredGroupId(null);
                    }}
                    onFocus={() => {
                      if (collapsed) setHoveredGroupId(item.id);
                    }}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                        setHoveredGroupId((current) => (current === item.id ? null : current));
                      }
                    }}
                  >
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-label={item.label}
                      title={collapsed ? item.label : undefined}
                      onClick={() => toggleGroup(item)}
                      className={cn(
                        'flex w-full items-center border-0 bg-transparent transition-colors',
                        index === 0 ? 'mt-0' : 'mt-3',
                        collapsed
                          ? 'justify-center rounded-lg px-2 py-2 text-[var(--a-text-soft)] hover:bg-[var(--a-surface-2)] hover:text-[var(--a-text)]'
                          : 'rounded-md px-2.5 pb-1 pt-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.13em] text-[var(--a-text-faint)] hover:text-[var(--a-text-soft)]',
                        collapsed &&
                          isActive &&
                          'bg-[var(--a-primary-soft)] text-[var(--a-primary-text)]',
                      )}
                    >
                      {!collapsed ? (
                        <>
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                          <ChevronDownIcon
                            className={cn(
                              'h-3.5 w-3.5 flex-shrink-0 text-[var(--a-text-faint)] transition-transform',
                              expanded && 'rotate-180',
                            )}
                          />
                        </>
                      ) : (
                        <CollapsedIcon
                          className={SIDEBAR_COLLAPSED_NAV_ICON_CLASS}
                          strokeWidth={2.25}
                          aria-hidden="true"
                        />
                      )}
                    </button>

                    {showCollapsedFlyout ? (
                      <div className="absolute left-[calc(100%+10px)] top-0 z-[80] hidden w-60 lg:block">
                        <div className="rounded-xl border border-[var(--a-line)] bg-[var(--a-surface)] p-2 shadow-[0_18px_42px_rgba(0,0,0,0.16)]">
                          <p className="m-0 px-2 pb-1.5 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.13em] text-[var(--a-text-faint)]">
                            {item.label}
                          </p>
                          <ul role="list" className="m-0 flex list-none flex-col gap-0.5 p-0">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon ?? item.collapsedIcon;
                              return (
                                <li key={child.path}>
                                  <a
                                    href={child.path}
                                    aria-current={child.active ? 'page' : undefined}
                                    onClick={(event) =>
                                      handleAnchorNavigate(
                                        event,
                                        child.path,
                                        onNavigate,
                                        onMobileOpenChange,
                                      )
                                    }
                                    className={cn(
                                      'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors',
                                      child.active
                                        ? 'bg-[var(--a-primary-soft)] font-medium text-[var(--a-primary-text)]'
                                        : 'text-[var(--a-text)] hover:bg-[var(--a-surface-2)]',
                                    )}
                                  >
                                    <ChildIcon className={SIDEBAR_NAV_ICON_CLASS} />
                                    <span className="truncate">{child.label}</span>
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    ) : null}

                    {!collapsed && expanded ? (
                      <ul role="list" className="mt-0.5 flex list-none flex-col gap-0.5 py-0.5">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon ?? item.collapsedIcon;
                          return (
                            <li key={child.path}>
                              <a
                                href={child.path}
                                aria-current={child.active ? 'page' : undefined}
                                onClick={(event) =>
                                  handleAnchorNavigate(
                                    event,
                                    child.path,
                                    onNavigate,
                                    onMobileOpenChange,
                                  )
                                }
                                className={cn(
                                  'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-colors',
                                  child.active
                                    ? 'bg-[var(--a-primary-soft)] font-medium text-[var(--a-primary-text)]'
                                    : 'text-[var(--a-text)] hover:bg-[var(--a-surface-2)]',
                                )}
                              >
                                <ChildIcon className={SIDEBAR_NAV_ICON_CLASS} />
                                <span className="truncate">{child.label}</span>
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              }

              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <a
                    href={item.path}
                    aria-current={item.active ? 'page' : undefined}
                    title={collapsed ? item.label : undefined}
                    onClick={(event) =>
                      handleAnchorNavigate(event, item.path, onNavigate, onMobileOpenChange)
                    }
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] transition-colors',
                      item.active
                        ? 'bg-[var(--a-primary-soft)] font-medium text-[var(--a-primary-text)]'
                        : 'text-[var(--a-text)] hover:bg-[var(--a-surface-2)]',
                      collapsed && 'justify-center px-2',
                    )}
                  >
                    {Icon ? <Icon className={SIDEBAR_NAV_ICON_CLASS} /> : null}
                    {!collapsed ? <span>{item.label}</span> : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {user ? (
          <div ref={userMenuRef} className="relative border-t border-[var(--a-line-soft)]">
            <div
              className={cn(
                'grid transform-gpu transition-[grid-template-rows,opacity,transform] duration-200 ease-out',
                userMenuOpen && !collapsed
                  ? 'grid-rows-[1fr] translate-y-0 opacity-100'
                  : 'grid-rows-[0fr] translate-y-1 opacity-0',
              )}
              aria-hidden={!userMenuOpen || collapsed}
              inert={!userMenuOpen || collapsed ? true : undefined}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="px-2.5 pb-2 pt-2" role="menu">
                  <div className="rounded-xl border border-[var(--a-line)] bg-[var(--a-surface)] p-2 shadow-[0_10px_24px_rgba(0,0,0,0.08)]">
                    <div className="flex items-center gap-2.5 px-1.5 py-1.5">
                      <span className="relative grid h-8 w-8 flex-shrink-0 place-items-center rounded-[9px] bg-[var(--a-primary-soft)] text-xs font-semibold text-[var(--a-primary-text)]">
                        {user.initials}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-[var(--a-text)]">
                          {user.displayName}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-[var(--a-text-soft)]">
                          {user.roleLabel}
                        </span>
                      </span>
                      <button
                        type="button"
                        aria-label={labels?.closeUserMenu ?? 'Close user menu'}
                        onClick={() => setUserMenuOpen(false)}
                        tabIndex={userMenuOpen && !collapsed ? 0 : -1}
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-[var(--a-text-faint)] transition-colors hover:bg-[var(--a-surface-2)] hover:text-[var(--a-text-soft)]"
                      >
                        <ChevronUpIcon className={SIDEBAR_NAV_ICON_CLASS} strokeWidth={2.25} />
                      </button>
                    </div>
                    <div className="mx-1.5 my-1.5 h-px bg-[var(--a-line-soft)]" />
                    {utilityItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="menuitem"
                          tabIndex={userMenuOpen && !collapsed ? 0 : -1}
                          onClick={() => {
                            setUserMenuOpen(false);
                            onMobileOpenChange(false);
                            item.onSelect();
                          }}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors',
                            item.active
                              ? 'bg-[var(--a-primary-soft)] text-[var(--a-primary-text)]'
                              : 'text-[var(--a-text)] hover:bg-[var(--a-surface-2)]',
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                          {item.badgeLabel ? <SidebarBadge label={item.badgeLabel} /> : null}
                        </button>
                      );
                    })}
                    <div className="mx-1.5 my-1 h-px bg-[var(--a-line-soft)]" />
                    <button
                      type="button"
                      role="menuitem"
                      tabIndex={userMenuOpen && !collapsed ? 0 : -1}
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-[var(--a-danger)] transition-colors hover:bg-[var(--a-surface-2)]"
                    >
                      <ArrowRightOnRectangleIcon className="h-3.5 w-3.5" />
                      {labels?.signOut ?? 'Sign out'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {userMenuOpen && !collapsed ? null : (
              <button
                ref={userMenuTriggerRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={openUserMenuLabel}
                data-testid="sidebar-user-menu-trigger"
                onClick={() => {
                  if (collapsed) {
                    onCollapsedChange(false);
                    return;
                  }
                  setUserMenuOpen((open) => !open);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 border-0 bg-transparent px-3.5 py-3 text-left transition-colors hover:bg-[var(--a-surface-2)]',
                  collapsed && 'justify-center px-2',
                )}
              >
                <span className="relative grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-[9px] bg-[var(--a-primary-soft)] text-xs font-semibold text-[var(--a-primary-text)]">
                  {user.initials}
                </span>
                {!collapsed ? (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-[var(--a-text)]">
                        <span className="truncate">{user.displayName}</span>
                        {user.badgeLabel ? (
                          <SidebarBadge
                            ariaLabel={user.badgeAriaLabel}
                            label={user.badgeLabel}
                            className="shrink-0"
                          />
                        ) : null}
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] text-[var(--a-text-soft)]">
                        {user.roleLabel}
                      </span>
                    </span>
                    <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-[var(--a-text-faint)]" />
                  </>
                ) : null}
              </button>
            )}
          </div>
        ) : null}
      </aside>
    </>
  );
}
